import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, CheckCircle2, XCircle, Loader2, AlertCircle } from "lucide-react";
import { createLegacyProposal } from "@/lib/legacyProposalsApi";
import { useToast } from "@/hooks/use-toast";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface OldProposal {
  id: number;
  client_id: number;
  internal_reference?: string;
  title: string;
  final_destination: string;
  status: string;
  created_at: string;
  updated_at: string;
  user_id?: number;
  passengers?: any[];
  legs?: any[];
  airport_services?: any[];
  transfers?: any[];
  flights?: any[];
  hotels?: any[];
  tours?: any[];
  attractions?: any[];
  tickets?: any[];
  additional_requests?: any[];
  handling_fees?: any;
  terms?: any;
}

interface ImportResult {
  success: number;
  failed: number;
  errors: Array<{ proposalId: number; error: string }>;
}

const ImportProposals = () => {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const { toast } = useToast();
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const canImport = userRole === "ceo" || userRole === "general_manager";

  useEffect(() => {
    if (!canImport) {
      navigate("/proposals-legacy");
    }
  }, [canImport, navigate]);

  if (!canImport) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to import proposals.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const mapStatus = (oldStatus: string): string => {
    const statusMap: Record<string, string> = {
      'draft': 'in_progress',
      'saved': 'sent',
      'submitted': 'approved',
      'confirmed': 'approved',
    };
    return statusMap[oldStatus] || 'in_progress';
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      setProgress(0);
      setResult(null);

      const text = await file.text();
      const proposals: OldProposal[] = JSON.parse(text);

      const importResult: ImportResult = {
        success: 0,
        failed: 0,
        errors: []
      };

      for (let i = 0; i < proposals.length; i++) {
        const oldProposal = proposals[i];
        setProgress(Math.round(((i + 1) / proposals.length) * 100));

        try {
          await importProposal(oldProposal);
          importResult.success++;
        } catch (error: any) {
          console.error(`Error importing proposal ${oldProposal.id}:`, error);
          importResult.failed++;
          importResult.errors.push({
            proposalId: oldProposal.id,
            error: error.message
          });
        }
      }

      setResult(importResult);
      toast({
        title: "Import completed",
        description: `Success: ${importResult.success}, Failed: ${importResult.failed}`,
      });
    } catch (error: any) {
      toast({
        title: "Import failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const importProposal = async (oldProposal: OldProposal) => {
    if (!user) throw new Error("User not authenticated");

    const toIsoString = (value?: string): string | undefined => {
      if (!value) return undefined;
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return undefined;
      return parsed.toISOString();
    };

    const mapRequestImages = (images: any): string[] | null => {
      if (!images) return null;
      if (Array.isArray(images)) {
        const mapped = images
          .map((img) => {
            if (typeof img === "string") return img.trim();
            if (img && typeof img === "object") {
              const path = img.image_path || img.path || img.url || img.imageUrl;
              return typeof path === "string" ? path.trim() : null;
            }
            return null;
          })
          .filter((v) => !!v) as string[];
        return mapped.length ? mapped : null;
      }
      if (typeof images === "string") {
        const trimmed = images.trim();
        return trimmed ? [trimmed] : null;
      }
      return null;
    };

    const createdAt = toIsoString(oldProposal.created_at);
    const updatedAt = toIsoString(oldProposal.updated_at) || createdAt;

    const payload: Record<string, any> = {
      client_id: oldProposal.client_id,
      internal_reference: oldProposal.internal_reference || `REF-${oldProposal.id}`,
      title: oldProposal.title,
      main_destination: oldProposal.final_destination,
      status: mapStatus(oldProposal.status),
      owner_id: user.id,
      created_by: user.id,
      created_at: createdAt,
      updated_at: updatedAt,
      pax_adults: 0,
      pax_children: 0,
      pax_infants: 0,
      passenger_mode_detailed: (oldProposal.passengers?.length || 0) > 0,
      language: "en",
      last_step: 11,
      passengers: (oldProposal.passengers || []).map((p) => ({
        full_name: p.full_name,
        passenger_type: p.passenger_type,
        passport_number: p.passport_number || null,
        passport_expiry: p.passport_expiry || null,
      })),
      trip_legs: (oldProposal.legs || []).map((l) => ({
        from_location: l.from_location,
        to_location: l.to_location,
        start_date: l.start_date,
        end_date: l.end_date,
      })),
      airport_services: (oldProposal.airport_services || []).map((s) => ({
        airport_service_id: s.airport_service_id,
        service_date: s.service_date,
        notes: s.notes || null,
        free_of_charge: s.use_custom_count === 1,
        foc_quantity: s.free_count || null,
      })),
      transfers: (oldProposal.transfers || []).map((t) => ({
        transfer_id: t.transfer_id,
        transfer_date: t.transfer_date,
        transfer_type: t.day || "arrival",
        notes: t.notes || null,
        free_of_charge: t.use_custom_count === 1,
        foc_quantity: t.free_count || null,
      })),
      flights: (oldProposal.flights || []).map((f) => ({
        flight_number: f.flight_number || "",
        airline: f.airline || "",
        departure_airport: f.departure_airport || "",
        arrival_airport: f.arrival_airport || "",
        departure_datetime: f.departure_datetime,
        arrival_datetime: f.arrival_datetime,
        flight_class: f.flight_class || null,
        booking_with_us: f.booking_with_us || false,
      })),
      accommodations: (oldProposal.hotels || []).map((h) => ({
        hotel_id: h.hotel_id,
        room_id: parseInt(h.room_type) || null,
        check_in: h.check_in,
        check_out: h.check_out,
        number_of_rooms: h.room_count || 1,
        meal_option: h.meal_options || null,
        cancellation_policy: h.cancellation_policy || null,
        booking_with_us: false,
      })),
      tours: (oldProposal.tours || []).map((t) => ({
        tour_id: t.tour_id,
        tour_date: t.tour_date,
        time_from: t.time_from || null,
        time_to: t.time_to || null,
        free_of_charge: t.use_custom_count === 1,
        foc_quantity: t.free_count || null,
      })),
      attractions: (oldProposal.attractions || []).map((a) => {
        const ticket = oldProposal.tickets?.find((t) => t.proposal_attraction_id === a.id);
        return {
          attraction_id: a.attraction_id,
          ticket_id: ticket?.ticket_id || null,
          attraction_date: a.date,
          time_from: a.from_time || null,
          time_to: a.to_time || null,
          ticket_quantity: ticket?.quantity || 1,
          notes: a.notes || null,
          free_of_charge: a.use_custom_count === 1 || (ticket && ticket.use_custom_count === 1),
          foc_quantity: a.free_count || null,
        };
      }),
      additional_requests: (oldProposal.additional_requests || []).map((r) => ({
        request_title: r.request_title,
        request_description: r.request_description || null,
        request_price: parseFloat(r.request_price || "0"),
        request_date: r.additional_request_date || null,
        request_time: r.additional_request_time || null,
        request_images: mapRequestImages(r.images),
      })),
      handling_fee: oldProposal.handling_fees
        ? [{
            fee_title: oldProposal.handling_fees.title || "Handling Fee",
            fee_description: oldProposal.handling_fees.description || null,
            fee_price: parseFloat(oldProposal.handling_fees.price || "0"),
          }]
        : [],
    };

    await createLegacyProposal(payload as any);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Import Proposals (Legacy)</h1>
          <p className="text-muted-foreground">Import proposals from old system JSON file into legacy base</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Upload JSON File</CardTitle>
            <CardDescription>
              Select the JSON file containing proposals from the old system. The file must contain an array of proposal objects with all related data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-12 h-12 mb-4 text-muted-foreground" />
                  <p className="mb-2 text-sm text-muted-foreground">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">JSON file with proposals data</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".json"
                  onChange={handleFileUpload}
                  disabled={importing}
                />
              </label>
            </div>

            {importing && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Importing proposals... {progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {result && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Successfully imported: {result.success}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span>Failed: {result.failed}</span>
                    </div>
                    {result.errors.length > 0 && (
                      <div className="mt-4">
                        <p className="font-semibold mb-2">Errors:</p>
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          {result.errors.map((err, idx) => (
                            <div key={idx} className="text-xs text-red-600">
                              Proposal {err.proposalId}: {err.error}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-semibold mb-2">Important Notes:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Make sure all referenced clients, hotels, tours, attractions, and transfers exist in the system</li>
              <li>New UUIDs will be generated for all imported proposals (old IDs are ignored)</li>
              <li>Status mapping: "saved" → "sent", "submitted" → "approved"</li>
              <li>Free of charge items are determined by use_custom_count = 1</li>
              <li>Large files may take several minutes to process</li>
            </ul>
          </AlertDescription>
        </Alert>
      </div>
    </DashboardLayout>
  );
};

export default ImportProposals;
